// Hook: Mind guard evaluation — pre-trade & algo guards
import { useCallback } from 'react';
import { useMind } from '../context/MindContext';
import { GUARD_META } from '../config/mindConstants';
import { detectRevengePattern } from '../utils/mindScoring';

export function useMindGuards() {
  const {
    settings, performance,
    isCheckedIn, energyScore, cooldownActive,
  } = useMind();

  // Evaluate all pre-trade guards for an order
  const evaluatePreTradeGuards = useCallback((orderData) => {
    if (!settings) return { allowed: true, guards: [] };

    const triggered = [];

    // 1. Pre-trade check missing
    if (settings.enablePreTradeCheck && !isCheckedIn) {
      triggered.push({ ...GUARD_META.PRE_TRADE_CHECK });
    }

    // 2. Loss streak cooldown
    if (settings.enableCooldown && cooldownActive) {
      triggered.push({ ...GUARD_META.LOSS_STREAK });
    }

    // 3. Overtrade count
    if (settings.enableOvertradingGuard && performance) {
      const ratio = (performance.tradesToday || 0) / settings.maxTradesPerDay;
      if (ratio >= 1) {
        triggered.push({
          ...GUARD_META.OVERTRADE_COUNT,
          severity: 'block',
          overridable: false,
          description: `You've reached your daily limit of ${settings.maxTradesPerDay} trades.`,
        });
      } else if (ratio >= 0.8) {
        triggered.push({
          ...GUARD_META.OVERTRADE_COUNT,
          description: `${performance.tradesToday}/${settings.maxTradesPerDay} trades used today (${Math.round(ratio * 100)}%).`,
        });
      }
    }

    // 4. Overtrade speed
    if (settings.enableOvertradingGuard && performance?.lastTradeTime) {
      const elapsed = (Date.now() - performance.lastTradeTime) / 1000;
      if (elapsed < settings.minTimeBetweenTrades) {
        triggered.push({
          ...GUARD_META.OVERTRADE_SPEED,
          description: `Only ${Math.round(elapsed)}s since last trade (min: ${settings.minTimeBetweenTrades}s).`,
        });
      }
    }

    // 5. Revenge trade
    if (settings.enableRevengeGuard && performance?.recentTrades) {
      if (detectRevengePattern(orderData, performance.recentTrades)) {
        triggered.push({ ...GUARD_META.REVENGE_TRADE });
      }
    }

    // 6. Risk per trade (HARD BLOCK)
    if (settings.enableRiskGuard && orderData.quantity && orderData.price) {
      const orderValue = parseFloat(orderData.quantity) * parseFloat(orderData.price);
      const capitalEstimate = 500000; // TODO: get from portfolio
      const riskPercent = (orderValue / capitalEstimate) * 100;
      if (riskPercent > settings.maxRiskPercent) {
        triggered.push({
          ...GUARD_META.RISK_PER_TRADE,
          description: `Order is ${riskPercent.toFixed(1)}% of capital (max: ${settings.maxRiskPercent}%).`,
        });
      }
    }

    // 7. Daily loss limit
    if (settings.enableDailyLossGuard && performance) {
      const lossRatio = performance.dailyPnL < 0
        ? Math.abs(performance.dailyPnL) / settings.dailyLossLimit
        : 0;
      if (lossRatio >= 1) {
        triggered.push({
          ...GUARD_META.DAILY_LOSS,
          severity: 'block',
          overridable: false,
          description: `Daily loss limit of ₹${settings.dailyLossLimit.toLocaleString()} reached.`,
        });
      } else if (lossRatio >= 0.8) {
        triggered.push({
          ...GUARD_META.DAILY_LOSS,
          description: `Daily P&L: ₹${performance.dailyPnL.toLocaleString()} (${Math.round(lossRatio * 100)}% of limit).`,
        });
      }
    }

    // 8. Low energy
    if (settings.enableEnergyGuard && energyScore < settings.energyThreshold) {
      triggered.push({
        ...GUARD_META.LOW_ENERGY,
        description: `Energy score is ${energyScore}/100 (threshold: ${settings.energyThreshold}).`,
      });
    }

    // Determine overall: blocked if any non-overridable guard fires
    const hasBlock = triggered.some(g => g.severity === 'block' && !g.overridable);
    const allowed = triggered.length === 0;

    return { allowed, blocked: hasBlock, guards: triggered };
  }, [settings, isCheckedIn, cooldownActive, performance, energyScore]);

  // Evaluate guards for algo auto-execution (simplified)
  const evaluateAlgoGuards = useCallback(() => {
    if (!settings) return { allowed: true, reason: null };

    if (settings.enableCooldown && cooldownActive) {
      return { allowed: false, reason: 'Loss streak cooldown active' };
    }

    if (settings.enableOvertradingGuard && performance) {
      if ((performance.tradesToday || 0) >= settings.maxTradesPerDay) {
        return { allowed: false, reason: 'Daily trade limit reached' };
      }
    }

    if (settings.enableDailyLossGuard && performance) {
      if (performance.dailyPnL < 0 && Math.abs(performance.dailyPnL) >= settings.dailyLossLimit) {
        return { allowed: false, reason: 'Daily loss limit reached' };
      }
    }

    if (settings.enableEnergyGuard && energyScore < settings.energyThreshold) {
      return { allowed: false, reason: `Energy too low (${energyScore})` };
    }

    return { allowed: true, reason: null };
  }, [settings, cooldownActive, performance, energyScore]);

  return { evaluatePreTradeGuards, evaluateAlgoGuards };
}
